/**
 * PartnerTryOnStudio - In-Store Module, Partner Level TryOn Studio
 *
 * This page integrates @nanokart/tryon-studio-sdk as an external plugin.
 * It treats the SDK as a generic integration unit  -  exactly how a 3rd-party
 * website developer would integrate it into their own website.
 *
 * Page responsibilities (host website):
 *   - Authenticate the partner (JWT via AuthContext)
 *   - Fetch the partner's nk_... API key
 *   - Provide the partner's inventory via a custom CatalogProvider
 *   - Mount <NanokartTryOnStudio> with config
 *
 * SDK responsibilities:
 *   - UI rendering (arc carousel, stage, photo upload, moments)
 *   - Try-on generation (POST /api/v1/tryonstudio/tryon)
 *   - Moments generation (POST /api/v1/tryonstudio/moments)
 *   - Credits deduction (handled server-side)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NanokartTryOnStudio } from '../../vendor/@nanokart/tryon-studio-sdk';
import { inventoryAPI, apiKeysAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { X, Sparkles } from 'lucide-react';
import TryOnColorChangePicker from '../../components/TryOnColorChangePicker';

// ─── Catalog Item Mapper ───────────────────────────────────────────────────
// Maps Nanokart's internal inventory product format → SDK CatalogItem schema

function mapInventoryProduct(product) {
  return {
    productId: product.product_id,
    name: product.product_name || product.name || 'Unnamed Product',
    imageUrl: product.tryon_input_image_url || product.tryon_input_thumbnail_url || '',
    thumbnailUrl: product.tryon_input_thumbnail_url || product.tryon_input_image_url || '',
    price: parseFloat(product.selling_price || product.mrp || 0),
    currency: 'INR',
    category: product.category || undefined,
    color: product.color || undefined,
    metadata: {
      skuId: product.sku_id,
      gender: product.gender,
      mrp: product.mrp,
      assignedMoment: product.assigned_moment,
      // ── AI Color Change passthrough ────────────────────────────────────
      // The SDK opaquely passes `metadata` through, so we can recover these
      // fields at the host level when handling onTryOnComplete events.
      dressType: product.dress_type,
      colorVariants: product.color_variants || [],
      colorChangeEnabled: !!product.color_change_enabled,
      colorChangeQuality: product.color_change_quality || 'standard',
    },
  };
}

// ─── Custom Catalog Provider ───────────────────────────────────────────────
// Uses Nanokart's inventoryAPI (includes JWT auth interceptor automatically)

function createPartnerCatalogProvider() {
  let cache = null;
  // Side-band map keyed by productId so the host (this file) can recover the
  // full SDK metadata blob — including the AI Color Change settings — when
  // onTryOnComplete fires (the event payload only carries productId).
  const fullById = new Map();

  return {
    capabilities: {
      supportsFiltering: false,
      supportsPagination: false,
      supportsSearch: true,
    },

    /** Host-only helper: returns the SDK metadata for a productId. */
    _getFullById(productId) {
      return fullById.get(productId) || null;
    },

    async list() {
      if (cache) return { items: cache, total: cache.length, hasMore: false };

      const response = await inventoryAPI.listProducts({
        page: 1,
        page_size: 100,
        status: 'active',
      });

      const products = response.data?.products || [];
      cache = products
        .filter((p) => p.tryon_input_image_url) // only items with a garment image
        .map(mapInventoryProduct);
      fullById.clear();
      cache.forEach((it) => fullById.set(it.productId, it));

      return { items: cache, total: cache.length, hasMore: false };
    },

    async getById(productId) {
      const result = await this.list();
      return result.items.find((item) => item.productId === productId) || null;
    },

    async search(query) {
      const result = await this.list();
      const q = query.toLowerCase();
      const filtered = result.items.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.category?.toLowerCase().includes(q) ||
          item.color?.toLowerCase().includes(q)
      );
      return { items: filtered, total: filtered.length, hasMore: false };
    },
  };
}

// ─── Loading Screen ────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        backgroundColor: '#fafafa',
        color: '#6b7280',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e5e7eb',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <p style={{ margin: 0, fontSize: '14px' }}>Loading TryOn Studio...</p>
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ─── No API Key Screen ─────────────────────────────────────────────────────

function NoApiKeyScreen({ onGenerate, isGenerating, authError, isTeamMember }) {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        backgroundColor: '#fafafa',
        color: '#374151',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {authError ? (
        <div>
          <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '16px' }}>Session Expired</p>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
            Your session has expired. Please log in again to continue.
          </p>
          <a
            href="/login"
            style={{
              display: 'inline-block',
              padding: '10px 24px',
              backgroundColor: '#6366f1',
              color: '#fff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Go to Login
          </a>
        </div>
      ) : isTeamMember ? (
        <div data-testid="no-apikey-team-member">
          <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '16px' }}>API Key Not Set Up</p>
          <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#6b7280', maxWidth: 420 }}>
            Your account admin hasn&apos;t generated an API key yet. Ask them to open
            <b> Integrations &rarr; API Keys</b> and generate one — then the TryOn Studio will be ready for you.
          </p>
        </div>
      ) : (
        <div>
          <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '16px' }}>API Key Required</p>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
            No API key found for your account. Generate one to use TryOn Studio.
          </p>
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            data-testid="no-apikey-generate-btn"
            style={{
              padding: '10px 24px',
              backgroundColor: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              opacity: isGenerating ? 0.7 : 1,
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate API Key'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function PartnerTryOnStudio() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);

  // Stable catalog provider  -  created once, not on every render
  const catalogProvider = useMemo(() => createPartnerCatalogProvider(), []);

  // AI Color Change — host-level state. The SDK does not natively support
  // color change; we listen to its onTryOnComplete event to capture the
  // tryon_key (already mapped to SDK's `cacheKey`) and render an external
  // picker as a fixed bottom strip, plus a floating preview card.
  const [latestTryOn, setLatestTryOn] = useState(null);
  const [colorPreview, setColorPreview] = useState(null); // { image_url, name, hex }

  const fetchApiKey = () => {
    apiKeysAPI.get()
      .then((response) => {
        const data = response.data;
        if (typeof data === 'object' && data?.api_key) {
          setApiKey(data.api_key);
          setIsTeamMember(!!data.is_team_member);
        } else {
          console.warn('[PartnerTryOnStudio] No API key returned:', data);
          if (typeof data === 'object') setIsTeamMember(!!data.is_team_member);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          setAuthError(true);
        } else {
          toast.error('Failed to load TryOn Studio');
        }
        setIsLoading(false);
      });
  };

  // Fetch the partner's nk_... API key on mount
  useEffect(() => {
    fetchApiKey();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateApiKey = () => {
    setIsGenerating(true);
    apiKeysAPI.regenerate()
      .then((response) => {
        const data = response.data;
        if (data?.api_key) {
          setApiKey(data.api_key);
          toast.success('API key generated successfully!');
        } else {
          toast.error('Failed to generate API key');
        }
        setIsGenerating(false);
      })
      .catch((err) => {
        console.error('[PartnerTryOnStudio] Failed to generate API key:', err);
        toast.error('Failed to generate API key');
        setIsGenerating(false);
      });
  };

  // ── AI Color Change host-side state ─────────────────────────────────────
  // We track the latest try-on the SDK reported (its cacheKey is the real S3
  // tryon_key) plus the resolved product so the picker knows whether to
  // render and which swatches to show.
  const handleTryOnCompleteWithColor = useCallback((event) => {
    const { productId, resultImageUrl, cacheKey } = event.payload || {};
    const meta = catalogProvider._getFullById(productId);
    setLatestTryOn({
      productId,
      productName: event.payload?.productName,
      tryonKey: cacheKey, // SDK already maps backend response.tryon_key → cacheKey
      resultImageUrl,
      product: meta ? {
        sku_id: meta.metadata?.skuId,
        color_variants: meta.metadata?.colorVariants || [],
        color_change_enabled: !!meta.metadata?.colorChangeEnabled,
        color_change_quality: meta.metadata?.colorChangeQuality || 'standard',
        dress_type: meta.metadata?.dressType,
      } : null,
    });
    setColorPreview(null); // discard any previous recoloring
  }, [catalogProvider]);

  if (isLoading) return <LoadingScreen />;
  if (!apiKey) return (
    <NoApiKeyScreen
      onGenerate={handleGenerateApiKey}
      isGenerating={isGenerating}
      authError={authError}
      isTeamMember={isTeamMember}
    />
  );

  return (
    <div style={{ position: 'relative', height: '100vh', maxHeight: '100vh' }}>
      <NanokartTryOnStudio
      // ── SDK Auth (external plugin integration) ──────────────────────────
      apiKey={apiKey}
      partnerId={user?.partner_id || user?.id || ''}

      // ── API Base URL ────────────────────────────────────────────────────
      // CRITICAL: the SDK is designed to run in a partner's ecosystem, so its
      // default base URL is `https://api.nanokart.ai` (production). When the
      // dashboard itself runs on stage / a preview env, the API key was
      // minted against the stage tables and would 401 against production.
      // We pin it to the same backend the rest of the dashboard talks to.
      // Fallback to the current origin so a missing/empty env var at build
      // time can never silently route SDK calls to production.
      apiBaseUrl={process.env.REACT_APP_BACKEND_URL || window.location.origin}

      // ── Catalog (partner's inventory) ───────────────────────────────────
      catalogProvider={catalogProvider}

      // ── Localisation ────────────────────────────────────────────────────
      currency="INR"
      timezone="Asia/Kolkata"

      // ── Branding ────────────────────────────────────────────────────────
      brandingConfig={{
        storeName: user?.store_name || user?.business_name || user?.name || 'Partner Store',
        primaryColor: '#6366f1',
        showPoweredBy: true,
      }}

      // ── Features ────────────────────────────────────────────────────────
      features={{
        inventoryBrowsing: true,
        moments: true,
        cart: false,       // Partner doesn't use cart in in-store context
        download: true,
        share: false,
        sizeRecommendation: true,
      }}

      // ── Event Callbacks (host website reacts to SDK events) ─────────────
      onReady={() => {
        console.log('[PartnerTryOnStudio] SDK ready');
      }}

      onTryOnComplete={handleTryOnCompleteWithColor}

      onMomentGenerated={(event) => {
        console.log('[PartnerTryOnStudio] Moment generated:', event.payload.momentType);
      }}

      onAddToCart={(event) => {
        // In-store context: show confirmation instead of actual cart
        toast.success(`${event.payload.productName}  -  Try-on saved!`);
      }}

      onError={(event) => {
        console.error('[PartnerTryOnStudio] SDK error:', event.payload);
        if (event.payload.code !== 'unknown/error') {
          toast.error(event.payload.message || 'An error occurred');
        }
      }}

      onSessionEnd={(event) => {
        console.log('[PartnerTryOnStudio] Session ended:', event.payload);
      }}

      // ── Layout ──────────────────────────────────────────────────────────
      style={{ height: '100vh', maxHeight: '100vh', borderRadius: 0 }}
    />

      {/* ── AI Color Change overlay UI (host-rendered, sibling to SDK) ─── */}
      {latestTryOn?.product?.color_change_enabled &&
       (latestTryOn.product.color_variants || []).filter(v => v && v.hex_code).length >= 2 &&
       latestTryOn.tryonKey && (
        <>
          {/* Fixed-bottom swatch picker — always visible after a try-on */}
          <div
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 12,
              transform: 'translateX(-50%)',
              zIndex: 1000,
              maxWidth: 'calc(100vw - 24px)',
            }}
            data-testid="partner-color-change-picker-wrap"
          >
            <TryOnColorChangePicker
              tryonKey={latestTryOn.tryonKey}
              product={latestTryOn.product}
              currentImageUrl={colorPreview?.image_url}
              onResult={(result) => {
                if (result.reset || !result.image_url) {
                  setColorPreview(null);
                } else {
                  setColorPreview({
                    image_url: result.image_url,
                    hex: result.hex,
                    name: result.name,
                  });
                }
              }}
            />
          </div>

          {/* Recolored preview card — floats top-right while a recolor is active */}
          {colorPreview?.image_url && (
            <div
              style={{
                position: 'fixed',
                top: 84,
                right: 16,
                width: 240,
                zIndex: 1000,
                background: 'white',
                borderRadius: 16,
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.18)',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                overflow: 'hidden',
              }}
              data-testid="partner-color-change-preview"
            >
              <div style={{
                padding: '8px 12px',
                background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(236,72,153,0.08))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <Sparkles style={{ width: 14, height: 14, color: '#8b5cf6', flexShrink: 0 }} />
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: '#334155',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {colorPreview.name || 'Recolored'}
                  </span>
                  <span
                    aria-label={`hex ${colorPreview.hex}`}
                    style={{
                      width: 12, height: 12, borderRadius: '50%',
                      background: colorPreview.hex || '#000',
                      border: '1px solid white',
                      boxShadow: '0 0 0 1px rgba(15,23,42,0.15)',
                      flexShrink: 0,
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setColorPreview(null)}
                  aria-label="Close recolored preview"
                  style={{
                    border: 'none', background: 'transparent',
                    color: '#64748b', cursor: 'pointer', padding: 2,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  data-testid="partner-color-change-preview-close"
                >
                  <X style={{ width: 14, height: 14 }} />
                </button>
              </div>
              <img
                src={colorPreview.image_url}
                alt={`Recolored to ${colorPreview.name}`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
